import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point as turfPoint, polygon as turfPolygon } from '@turf/helpers';
import { calculateEnrollment, getEarliestEnrollmentData } from './schoolUtils';
import { formatAcademicValue } from './academicUtils';

const DRIVE_TIMES = [5, 10, 15];

const RING_DRIVE_LABELS = {
  5: 'Within 5-min drive',
  10: 'Within 10-min drive',
  15: 'Within 15-min drive',
};

/** Innermost target drive-time ring that contains a lat/lng, or null if none. */
export function getInnermostDriveTime(lat, lng, geoJsonByDt) {
  if (lat == null || lng == null || !geoJsonByDt) return null;
  const pt = turfPoint([lng, lat]);
  for (const dt of DRIVE_TIMES) {
    const feature = geoJsonByDt[dt];
    if (!feature?.geometry?.coordinates) continue;
    try {
      const poly = turfPolygon(feature.geometry.coordinates);
      if (booleanPointInPolygon(pt, poly)) return dt;
    } catch {
      // skip malformed polygon
    }
  }
  return null;
}

/** Human-readable 5/10/15 ring copy for a point against campus drive-time polygons. */
export function driveTimeLabelForPoint(lat, lng, geoJsonByDt) {
  const dt = getInnermostDriveTime(lat, lng, geoJsonByDt);
  return RING_DRIVE_LABELS[dt] || 'Beyond 15-min drive';
}

/** True when at least one geocoded pin already has stored ESRI minutes. */
export function hasStoredPreciseDriveTimes(markers) {
  return (markers || []).some(
    (m) => m.lat != null && m.lng != null && typeof m.driveTimeMinutes === 'number'
  );
}

/**
 * Map a median (precise minutes or polygon zone) onto the 5/10/15 ring
 * users should treat as the primary catchment.
 */
export function focusDriveTimeFromMedian(preciseMinutes, medianZone) {
  if (typeof preciseMinutes === 'number' && Number.isFinite(preciseMinutes)) {
    if (preciseMinutes <= 5) return 5;
    if (preciseMinutes <= 10) return 10;
    if (preciseMinutes <= 15) return 15;
    return 'outside';
  }
  if (medianZone === 5 || medianZone === 10 || medianZone === 15 || medianZone === 'outside') {
    return medianZone;
  }
  return null;
}

/** Advisory headline + detail for the recommended drive-time ring. */
export function focusAdviceCopy(focusDriveTime, { preciseMinutes, usePreciseMedian } = {}) {
  if (focusDriveTime == null) return null;
  const rounded = typeof preciseMinutes === 'number' && Number.isFinite(preciseMinutes)
    ? Math.round(preciseMinutes)
    : null;
  if (focusDriveTime === 'outside') {
    return {
      headline: 'Focus beyond the 15-minute drive time',
      detail: usePreciseMedian && rounded != null
        ? `The typical student drives about ${rounded} minutes. Half of students live farther than all mapped rings — treat transportation as a core planning issue.`
        : 'Half of students live beyond a 15-minute drive. Treat transportation and retention as core planning issues.',
    };
  }
  return {
    headline: `Focus on the ${focusDriveTime}-minute drive time`,
    detail: usePreciseMedian && rounded != null
      ? `The typical student drives about ${rounded} minutes. Half of students live within this ring or closer — use it as the primary catchment.`
      : 'Half of students live within this ring or closer. Use it as the primary catchment when reading market share and nearby competition.',
  };
}

/** Parse optional CSV new_to_school values. Empty / unrecognized → null. */
export function parseNewToSchool(value) {
  if (value == null) return null;
  const v = String(value).trim().toLowerCase();
  if (!v) return null;
  if (['1', 'true', 'yes', 'y', 'new'].includes(v)) return true;
  if (['0', 'false', 'no', 'n', 'existing'].includes(v)) return false;
  return null;
}

/** Neighborhood name, or city + ZIP when Google has no neighborhood. */
export function neighborhoodDisplayLabel(neighborhood, city, zip) {
  const hood = typeof neighborhood === 'string' ? neighborhood.trim() : '';
  if (hood) return hood;
  const parts = [city, zip].map((p) => (p == null ? '' : String(p).trim())).filter(Boolean);
  return parts.join(' ') || 'Unknown';
}

/**
 * Enrollment (selected grades), 5-year trend, and overall academic rating
 * for a nearby-school enrollment payload.
 */
const CURRENT_ENROLLMENT_YEAR = 2024;
const COMPARISON_ENROLLMENT_YEAR = 2019;

function schoolYearRange(startYear) {
  return `${startYear}-${startYear + 1}`;
}

export function nearbySchoolMetrics(schoolData, selectedGrades) {
  const currentYear = schoolYearRange(CURRENT_ENROLLMENT_YEAR);
  const comparisonYear = schoolYearRange(COMPARISON_ENROLLMENT_YEAR);
  const empty = {
    enrollment: null,
    enrollmentLabel: '—',
    enrollmentLine: `${currentYear} Enrollment: —`,
    trendPct: null,
    trendLabel: '—',
    trendLine: `5-Year Enrollment Trend: —`,
    academicLabel: '—',
    academicLine: `${currentYear} Rating: —`,
    academicMetric: null,
  };
  if (!schoolData) return empty;

  const grades = selectedGrades?.length ? selectedGrades : [];
  const current = grades.length
    ? calculateEnrollment(schoolData.enrollment_by_grade?.current, grades)
    : null;
  const comparisonData = getEarliestEnrollmentData(schoolData);
  const comparison = comparisonData && grades.length
    ? calculateEnrollment(comparisonData, grades)
    : null;
  const hasComparison = comparison != null && comparison > 0 && current != null;
  const trendPct = hasComparison ? ((current - comparison) / comparison) * 100 : null;

  let trendLabel = `New / no ${comparisonYear} data`;
  if (hasComparison) {
    const rounded = Math.round(trendPct);
    trendLabel = `${rounded > 0 ? '+' : ''}${rounded}% since ${comparisonYear}`;
  }

  const overall = schoolData.academics?.overall;
  const academicLabel = overall ? formatAcademicValue(overall) : '—';
  const enrollmentLabel = current != null ? current.toLocaleString() : '—';

  return {
    enrollment: current,
    enrollmentLabel,
    enrollmentLine: `${currentYear} Enrollment: ${enrollmentLabel}`,
    trendPct,
    trendLabel,
    trendLine: `5-Year Enrollment Trend: ${trendLabel}`,
    academicLabel,
    academicLine: `${currentYear} Rating: ${academicLabel}`,
    academicMetric: overall || null,
  };
}

export function parseOverpassAmenities(payload) {
  const elements = payload?.elements || [];
  const seen = new Set();
  const places = [];
  elements.forEach((el) => {
    const lat = el.lat ?? el.center?.lat;
    const lng = el.lon ?? el.center?.lon;
    if (lat == null || lng == null) return;
    const amenity = el.tags?.amenity;
    if (amenity !== 'library' && amenity !== 'community_centre') return;
    const kind = amenity === 'library' ? 'library' : 'community_centre';
    const name = el.tags?.name || (kind === 'library' ? 'Library' : 'Community center');
    const key = `${kind}:${lat}:${lng}:${name}`;
    if (seen.has(key)) return;
    seen.add(key);
    places.push({
      id: `${el.type || 'n'}-${el.id}`,
      lat,
      lng,
      name,
      kind,
    });
  });
  return places;
}

export function overpassAmenitiesQuery(south, west, north, east) {
  return `[out:json][timeout:25];
(
  node["amenity"="library"](${south},${west},${north},${east});
  way["amenity"="library"](${south},${west},${north},${east});
  node["amenity"="community_centre"](${south},${west},${north},${east});
  way["amenity"="community_centre"](${south},${west},${north},${east});
);
out center;`;
}

export function summarizeNeighborhoods(markers) {
  const groups = {};
  (markers || []).forEach((m) => {
    const label = neighborhoodDisplayLabel(m.neighborhood, m.city, m.zipCode || m.zip_code);
    if (!groups[label]) {
      groups[label] = { label, count: 0, minutes: [] };
    }
    groups[label].count += 1;
    if (typeof m.driveTimeMinutes === 'number' && Number.isFinite(m.driveTimeMinutes)) {
      groups[label].minutes.push(m.driveTimeMinutes);
    }
  });
  const total = (markers || []).length || 1;
  return Object.values(groups)
    .map((g) => {
      const sorted = [...g.minutes].sort((a, b) => a - b);
      let medianMinutes = null;
      if (sorted.length) {
        const mid = Math.floor(sorted.length / 2);
        medianMinutes = sorted.length % 2 === 1
          ? sorted[mid]
          : (sorted[mid - 1] + sorted[mid]) / 2;
      }
      return {
        label: g.label,
        count: g.count,
        share: Math.round((g.count / total) * 100),
        medianMinutes,
      };
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export const OUTSIDE_DISTRICT_LABEL = 'Outside district boundaries';

const districtFeatureName = (feature) =>
  feature?.properties?.NAME
  || feature?.properties?.name
  || feature?.properties?.DISTRICT
  || 'Unknown District';

const bboxArea = (bounds) => {
  if (!bounds) return Number.POSITIVE_INFINITY;
  return (bounds.north - bounds.south) * (bounds.east - bounds.west);
};

export function districtLabelForPoint(lat, lng, districtGeoJSON) {
  if (lat == null || lng == null || !districtGeoJSON?.features?.length) return null;
  const pt = turfPoint([lng, lat]);
  let bestName = null;
  let bestArea = Number.POSITIVE_INFINITY;
  districtGeoJSON.features.forEach((feature) => {
    if (!feature?.geometry) return;
    const bounds = geojsonBounds(feature);
    if (bounds && (lat < bounds.south || lat > bounds.north || lng < bounds.west || lng > bounds.east)) {
      return;
    }
    try {
      if (!booleanPointInPolygon(pt, feature)) return;
    } catch {
      return;
    }
    const area = bboxArea(bounds);
    const name = districtFeatureName(feature);
    if (area < bestArea || (area === bestArea && (!bestName || name.localeCompare(bestName) < 0))) {
      bestArea = area;
      bestName = name;
    }
  });
  return bestName;
}

export function summarizeDistricts(markers, districtGeoJSON) {
  if (!districtGeoJSON?.features?.length) return [];
  const groups = {};
  (markers || []).forEach((m) => {
    if (m.lat == null || m.lng == null) return;
    const label = districtLabelForPoint(m.lat, m.lng, districtGeoJSON) || OUTSIDE_DISTRICT_LABEL;
    if (!groups[label]) {
      groups[label] = { label, count: 0, minutes: [] };
    }
    groups[label].count += 1;
    if (typeof m.driveTimeMinutes === 'number' && Number.isFinite(m.driveTimeMinutes)) {
      groups[label].minutes.push(m.driveTimeMinutes);
    }
  });
  const placed = Object.values(groups);
  if (placed.length === 0) return [];
  const total = placed.reduce((sum, g) => sum + g.count, 0) || 1;
  return placed
    .map((g) => {
      const sorted = [...g.minutes].sort((a, b) => a - b);
      let medianMinutes = null;
      if (sorted.length) {
        const mid = Math.floor(sorted.length / 2);
        medianMinutes = sorted.length % 2 === 1
          ? sorted[mid]
          : (sorted[mid - 1] + sorted[mid]) / 2;
      }
      return {
        label: g.label,
        count: g.count,
        share: Math.round((g.count / total) * 100),
        medianMinutes,
      };
    })
    .sort((a, b) => {
      if (a.label === OUTSIDE_DISTRICT_LABEL) return 1;
      if (b.label === OUTSIDE_DISTRICT_LABEL) return -1;
      return b.count - a.count || a.label.localeCompare(b.label);
    });
}

export function studentPinColor(newToSchool) {
  if (newToSchool === true) return '#0ea5e9';
  if (newToSchool === false) return '#16a34a';
  return '#64748b';
}

export function geojsonBounds(feature) {
  if (!feature?.geometry?.coordinates) return null;
  let south = 90;
  let north = -90;
  let west = 180;
  let east = -180;
  const walk = (coords) => {
    if (!Array.isArray(coords) || coords.length === 0) return;
    if (typeof coords[0] === 'number') {
      const [lng, lat] = coords;
      if (lat == null || lng == null) return;
      south = Math.min(south, lat);
      north = Math.max(north, lat);
      west = Math.min(west, lng);
      east = Math.max(east, lng);
      return;
    }
    coords.forEach(walk);
  };
  walk(feature.geometry.coordinates);
  if (south > north) return null;
  return { south, west, north, east };
}

export function bboxKey(bounds, decimals = 3) {
  if (!bounds) return '';
  const r = (n) => Number(n).toFixed(decimals);
  return `${r(bounds.south)},${r(bounds.west)},${r(bounds.north)},${r(bounds.east)}`;
}

const BUS_STOP_ALIASES = new Set(['bus_stop', 'bus', 'busstop', 'stop', 'bus_stops', 'busstops']);
const HEALTH_CENTER_ALIASES = new Set([
  'health_center', 'health', 'clinic', 'medical', 'healthcare',
  'healthcentre', 'health_centre', 'healthcenter',
]);
const RECREATION_CENTER_ALIASES = new Set([
  'recreation_center', 'recreationcenter', 'rec_center', 'rec',
  'recreation', 'rec_centre', 'recreation_centre',
]);

export const PLACE_TYPE_COLORS = {
  bus_stop: '#c2410c',
  health_center: '#be185d',
  recreation_center: '#15803d',
  other: '#4338ca',
};

function titleCasePlaceType(value) {
  const words = String(value ?? '').trim().replace(/[_-]+/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'Other';
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .slice(0, 80);
}

/** Canonical place kind + display label from a CSV/API type string. */
export function parsePlaceType(value) {
  const raw = String(value ?? '').trim();
  const key = raw.toLowerCase().replace(/[\s-]+/g, '_');
  if (!key) return { kind: 'other', label: 'Other' };
  if (BUS_STOP_ALIASES.has(key)) return { kind: 'bus_stop', label: 'Bus stop' };
  if (HEALTH_CENTER_ALIASES.has(key)) return { kind: 'health_center', label: 'Health center' };
  if (RECREATION_CENTER_ALIASES.has(key)) return { kind: 'recreation_center', label: 'Recreation center' };
  return { kind: 'other', label: titleCasePlaceType(raw) };
}

export function placeTypeColor(kind) {
  return PLACE_TYPE_COLORS[kind] || PLACE_TYPE_COLORS.other;
}

export function placeTypeLetter(kind) {
  if (kind === 'bus_stop') return 'B';
  if (kind === 'health_center') return 'H';
  if (kind === 'recreation_center') return 'R';
  return 'P';
}

/** How far the portfolio overlay reaches beyond the campus drive-time rings. */
export const PORTFOLIO_SCOPES = {
  DRIVE_TIME: 'drive-time',
  COUNTY: 'county',
  ALL: 'all',
};

const COUNTY_SUFFIX = /\s+(county|parish|borough|census area|municipality)$/;

/**
 * Comparison key for a free-text county value: "Jefferson County." → "jefferson".
 * location_addresses.county is operator-entered, so suffixes and casing vary.
 */
export function normalizeCounty(value) {
  const raw = String(value ?? '').trim().toLowerCase().replace(/[.,]/g, '');
  if (!raw) return '';
  return raw.replace(COUNTY_SUFFIX, '').replace(/\s+/g, ' ').trim();
}

/** County keys are only comparable within a state — "Jefferson County" exists in 25 of them. */
function countyStateKey(school) {
  const county = normalizeCounty(school?.county);
  if (!county) return '';
  const state = String(school?.state_abbr || school?.state || '').trim().toLowerCase();
  return `${county}|${state}`;
}

const EARTH_RADIUS_MILES = 3958.8;

export function haversineMiles(lat1, lng1, lat2, lng2) {
  if ([lat1, lng1, lat2, lng2].some((v) => typeof v !== 'number' || !Number.isFinite(v))) {
    return null;
  }
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MILES * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Portfolio campuses the requested scope adds *beyond* the drive-time rings.
 * Anything already in `nearbyUuids` is left to the existing nearby-schools path
 * so the two sets never render duplicate markers.
 */
export function portfolioSchoolsOutsideDriveTimes({
  portfolioSchools,
  scope,
  targetSchool,
  nearbyUuids,
} = {}) {
  if (scope !== PORTFOLIO_SCOPES.COUNTY && scope !== PORTFOLIO_SCOPES.ALL) return [];
  const list = portfolioSchools || [];
  if (list.length === 0) return [];

  const targetKey = countyStateKey(targetSchool);
  if (scope === PORTFOLIO_SCOPES.COUNTY && !targetKey) return [];

  const excluded = nearbyUuids instanceof Set ? nearbyUuids : new Set(nearbyUuids || []);
  const targetUuid = targetSchool?.uuid;

  return list.filter((school) => {
    if (!school?.uuid || school.uuid === targetUuid) return false;
    if (excluded.has(school.uuid)) return false;
    if (school.latitude == null || school.longitude == null) return false;
    if (scope === PORTFOLIO_SCOPES.COUNTY && countyStateKey(school) !== targetKey) return false;
    return true;
  });
}
